import {
    ACTIVATE_UNSAVED_DATA_PROTECTION,
    CHANGE_VIEW_TITLE,
    COLUMN_VISIBILITY_CHANGE_ACTION,
    ColumnVisibilityChangeAction,
    CreateUserViewSettingsActionTypes,
    DELETE_USER_VIEW_SETTINGS,
    DELETE_USER_VIEW_SETTINGS_ERROR,
    DISABLE_UNSAVED_DATA_PROTECTION,
    FETCH_TOP_USER_VIEW_SETTINGS,
    FETCH_TOP_USER_VIEW_SETTINGS_ERROR,
    FETCH_TOP_USER_VIEW_SETTINGS_SUCCESS,
    FETCH_USER_VIEW_SETTINGS,
    FETCH_USER_VIEW_SETTINGS_ERROR,
    FETCH_USER_VIEW_SETTINGS_SUCCESS,
    FetchTopUserViewSettingsActionTypes,
    FetchUserViewSettingsActionTypes,
    HANDLE_PAGE_NUMBER_CHANGE,
    HANDLE_ROWS_PER_PAGE_CHANGE,
    HANDLE_SEARCH_CHANGE,
    INCREMENT_USAGE_COUNT,
    INCREMENT_USAGE_COUNT_ERROR,
    INCREMENT_USAGE_COUNT_SUCCESS,
    IncrementUsageCountActionTypes,
    INITIAL_USER_VIEW_SETTINGS,
    REPLACE_USER_VIEW_SETTINGS, REPLACE_USER_VIEW_SETTINGS_ERROR, REPLACE_USER_VIEW_SETTINGS_SUCCESS,
    SEND_USER_TO_PAGE,
    SEND_USER_TO_PAGE_SUCCESS,
    SET_LIST_VIEW_COLUMNS,
    SetListViewColumnsAction,
    UnsavedDataProtectionActionTypes,
    USER_VIEW_SETTINGS_CHANGE,
    VIEW_DATA_FILTER_ADD,
    VIEW_DATA_FILTER_REMOVE,
    VIEW_SORT_CHANGE,
    ViewActionTypes,
    DELETE_USER_VIEW_SETTINGS_SUCCESS, DeleteUserViewSettingsActionTypes, ReplaceUserViewSettingsActionTypes
} from "../../actions/common/UserViewSettingsActions";
import {
    errorFetchingRequestState,
    getInitialRequestState,
    RequestState,
    startFetchingRequestState,
    successFetchingRequestState
} from "../../models/common/RequestState";
import {
    getInitialViewSettings,
    UserViewColumnState,
    UserViewSettings,
     UserViewSettingsList,
    ViewIdentifiable,
    ViewSettingsList
} from "../../models/common/ViewSettings";
import {isDateFilter} from "../../models/common/DataFilter";
import {Nullable} from "../../models/common/Nullable";
import {ColumnList, sortModelToOrderBy} from "../../models/common/DataTable";

export interface Redirect extends ViewIdentifiable {
    path: string
}

export interface ColumnListState {
    [ key: string ]: ColumnList
}

export interface ViewSettingsState {
    unsavedDataProtection: boolean
    currentViewTitle: string
    redirect: Nullable<Redirect>
    settings: ViewSettingsList
    userViewSettings: UserViewSettingsList
    //the view settings the user created earlier will be held in this object, after fetched from backend
    defaultViewSettings: { [ key: string ]: UserViewSettings[] }
    columns: ColumnListState
    request: RequestState
    incrementRequest: RequestState
}

export type ViewSettingsReducerActionTypes = ViewActionTypes
    | FetchUserViewSettingsActionTypes
    | ColumnVisibilityChangeAction
    | SetListViewColumnsAction
    | IncrementUsageCountActionTypes
    | CreateUserViewSettingsActionTypes
    | FetchTopUserViewSettingsActionTypes
    | DeleteUserViewSettingsActionTypes
    | ReplaceUserViewSettingsActionTypes

const initialState: ViewSettingsState = {
    unsavedDataProtection: false,
    settings: {},
    userViewSettings: {},
    defaultViewSettings: {},
    currentViewTitle: '',
    redirect: null,
    columns: {},
    request: getInitialRequestState(),
    incrementRequest: getInitialRequestState()
};


export function ViewSettingsReducer(
    state = initialState,
    action: ViewSettingsReducerActionTypes | UnsavedDataProtectionActionTypes
): ViewSettingsState {

    switch( action.type ) {
        case INITIAL_USER_VIEW_SETTINGS: {
            return {
                ...state,
                userViewSettings: pushUserViewSettings(
                    action.viewIdentifier,
                    {
                        ...getInitialViewSettings( action.viewIdentifier, action.userId?.rawValue ),
                        ...action.defaultViewSettings
                    },
                    state.userViewSettings
                )
            };
        }
        case FETCH_USER_VIEW_SETTINGS:
            return {
                ...state,
                request: {
                    ...state.request,
                    isFetching: true,
                    error: null,
                }
            };
        case FETCH_USER_VIEW_SETTINGS_SUCCESS:
            return {
                ...state,
                userViewSettings: pushUserViewSettings( action.viewIdentifier, action.settings, state.userViewSettings ),
                columns: {
                    ...state.columns,
                    [ action.viewIdentifier ]: setInitialColumnVisibility( state.columns[ action.viewIdentifier ], action.settings.columnState )
                },
                request: {
                    isFetching: false,
                    error: null,
                    lastRequestSuccess: Date.now()
                }
            };
        case FETCH_USER_VIEW_SETTINGS_ERROR:
            return {
                ...state,
                request: {
                    ...state.request,
                    isFetching: false,
                    error: action.error
                }
            };
        case VIEW_SORT_CHANGE:
            if( action.viewIdentifier in state.userViewSettings )  {
                return {
                    ...state,
                    userViewSettings: {
                        ...state.userViewSettings,
                        [ action.viewIdentifier ]: {
                            ...state.userViewSettings[ action.viewIdentifier ],
                            orderByFields: sortModelToOrderBy( action.orderBy )
                        },
                    }
                };
            } else { return state; }
        case HANDLE_SEARCH_CHANGE:
            if( action.viewIdentifier in state.userViewSettings ) {
                return {
                    ...state,
                    userViewSettings: {
                        ...state.userViewSettings,
                        [action.viewIdentifier]: {
                            ...state.userViewSettings[ action.viewIdentifier ],
                            searchValue: action.searchValue
                        }
                    }
                };
            } else {
                return { ...state };
            }
        case COLUMN_VISIBILITY_CHANGE_ACTION:

            if( action.viewIdentifier in state.userViewSettings ) {
                let newColumnState: UserViewColumnState[] = [];
                const oldColumnState = state.userViewSettings[ action.viewIdentifier ].columnState
                    .filter( col => col.columnName !== action.columnIdentifier );
                
                //keep only visibility = false in the array. since true is the default
                if( ! action.isVisible ) {
                    newColumnState = [
                        ...oldColumnState,
                        { columnName: action.columnIdentifier, isVisible: action.isVisible }
                    ];
                } else {
                    newColumnState = [ ...oldColumnState ];
                }

                return {
                    ...state,
                    userViewSettings: {
                        ...state.userViewSettings,
                        [ action.viewIdentifier ]: {
                            ...state.userViewSettings[ action.viewIdentifier ],
                            columnState: newColumnState
                        }
                    },
                    columns: {
                        ...state.columns,
                        [ action.viewIdentifier ]: setColumnVisibility(
                            state.columns[ action.viewIdentifier ],
                            action.columnIdentifier,
                            action.isVisible
                        )
                    }
                }
            } else {
                return { ...state };
            }
        case SET_LIST_VIEW_COLUMNS:
            return {
                ...state,
                columns: {
                    ...state.columns,
                    [ action.viewIdentifier ]: action.columns
                }
            };
        case HANDLE_ROWS_PER_PAGE_CHANGE:
            if( action.viewIdentifier in state.userViewSettings ) {
                return {
                    ...state,
                    userViewSettings: {
                        ...state.userViewSettings,
                        [ action.viewIdentifier ]: {
                            ...state.userViewSettings[ action.viewIdentifier ],
                            numberOfEntriesPerPage: action.rowsPerPage,
                        }
                    }
                };
            } else
                return state;
        case HANDLE_PAGE_NUMBER_CHANGE:
            if( action.viewIdentifier in state.userViewSettings ) {
                return {
                    ...state,
                    userViewSettings: {
                        ...state.userViewSettings,
                        [ action.viewIdentifier ]: {
                            ...state.userViewSettings[ action.viewIdentifier ],
                            selectedPageNumber: action.params.page
                        }
                    }
                };
            } else
                return state;
        case VIEW_DATA_FILTER_ADD:
            if( ! state.userViewSettings[ action.viewIdentifier ]) {
                console.log("ERROR: ViewSettingsReducer.userViewSettings does not contain property with name: "+ action.viewIdentifier );
                return state;
            }

            if( action.filter.type === "ID" && action.filter.selectedValue === "0" ) {
                return state;
            }

            return {
                ...state,
                userViewSettings: {
                    ...state.userViewSettings,
                    [ action.viewIdentifier ]: {
                        ...state.userViewSettings[ action.viewIdentifier ],
                        activeFilters: [ ...state.userViewSettings[ action.viewIdentifier ].activeFilters, action.filter ],
                    }
                }
            };
        case VIEW_DATA_FILTER_REMOVE:
            if( ! state.userViewSettings[ action.viewIdentifier ]) {
                console.log("ERROR: ViewSettingsReducer.userViewSettings does not contain property with name: "+ action.viewIdentifier );
                return state;
            }

            return {
                ...state,
                userViewSettings: {
                    ...state.userViewSettings,
                    [ action.viewIdentifier ]: {
                        ...state.userViewSettings[ action.viewIdentifier ],
                        activeFilters: state.userViewSettings[ action.viewIdentifier ].activeFilters.filter( filter =>
                            filter.identifier !== action.filter.identifier || ( filter.identifier === action.filter.identifier && filter.selectedValue !== action.filter.selectedValue )
                        ),
                    }
                }
            };
        case CHANGE_VIEW_TITLE:
            return {
                ...state,
                currentViewTitle: action.title
            };
        case SEND_USER_TO_PAGE:
            return {
                ...state,
                redirect: {
                    path: action.path,
                    viewIdentifier: action.viewIdentifier
                }
            };
        case SEND_USER_TO_PAGE_SUCCESS:
            return {
                ...state,
                redirect: null
            };
        case FETCH_TOP_USER_VIEW_SETTINGS:
            return {
                ...state,
                defaultViewSettings: {
                    ...state.defaultViewSettings,
                    [ action.viewIdentifier ]: []
                },
                request: startFetchingRequestState()
            };
        case FETCH_TOP_USER_VIEW_SETTINGS_SUCCESS:
            return {
                ...state,
                defaultViewSettings: {
                    ...state.defaultViewSettings,
                    [ action.viewIdentifier ]: action.topSettings
                },
                request: successFetchingRequestState()
            };
        case FETCH_TOP_USER_VIEW_SETTINGS_ERROR:
            return {
                ...state,
                request: errorFetchingRequestState( action.error )
            };
        case USER_VIEW_SETTINGS_CHANGE:
            return {
                ...state,
                userViewSettings: {
                    ...state.userViewSettings,
                    [ action.viewIdentifier ]: action.userViewSettings
                }
            };
        case INCREMENT_USAGE_COUNT:
            return {
                ...state,
                incrementRequest: startFetchingRequestState()
            };
        case INCREMENT_USAGE_COUNT_SUCCESS:
            return {
                ...state,
                incrementRequest: successFetchingRequestState()
            };
        case INCREMENT_USAGE_COUNT_ERROR:
            return {
                ...state,
                incrementRequest: errorFetchingRequestState(action.error)
            };

        case REPLACE_USER_VIEW_SETTINGS:
        case DELETE_USER_VIEW_SETTINGS:
            return {
                ...state,
                request: startFetchingRequestState(),
            };
        case REPLACE_USER_VIEW_SETTINGS_SUCCESS:
        case DELETE_USER_VIEW_SETTINGS_SUCCESS:
            return {
                ...state,
                request: successFetchingRequestState()
            };
        case REPLACE_USER_VIEW_SETTINGS_ERROR:
        case DELETE_USER_VIEW_SETTINGS_ERROR:
            return {
                ...state,
                request: errorFetchingRequestState( action.error )
            };
        case ACTIVATE_UNSAVED_DATA_PROTECTION:
            return {
                ...state,
                unsavedDataProtection: true
            };
        case DISABLE_UNSAVED_DATA_PROTECTION:
            return {
                ...state,
                unsavedDataProtection: false
            };
        default:
            return state;
    }
}

const setInitialColumnVisibility = ( columns: Nullable< ColumnList >, userViewColState: UserViewColumnState[] ): ColumnList => {
    let userViewState: { [key: string]: UserViewColumnState } = {};

    userViewColState.forEach(viewColState => {
        userViewState = {
            ...userViewState,
            [ viewColState.columnName ]: viewColState
        };
    });

    return columns?.map(col => {
        if ( userViewState[ col.field ] ) {
            return {...col, isVisible: userViewState[ col.field ].isVisible };
        } else {
            return {...col};
        }
    }) ?? [];

}

const setColumnVisibility = ( columns: ColumnList, columnIdentifier: string, isVisible: boolean ): ColumnList =>
    columns.map( col => {
        if( col.field === columnIdentifier ) {
            return { ...col, isVisible: isVisible };
        } else {
            return {  ...col };
        }
    } );


/**
 * replaces an object in array at the same position, if needle is equal to value of state object with property (needle property)
 * 
 * @param haystack array of object of type T
 * @param needle the value to check against
 * @param needleProperty the name of the property that holds the value in the state object to check needle against
 * @param replacement the new object to insert the location of the old object
 */
function replaceObjectInArray<T extends { [Key: string]: any }, S>( haystack: T[], needle: S, needleProperty: string, replacement: T ): T[] {
    return haystack.map(  ( object: T ) => ( needleProperty in object && needle === object[ needleProperty ] ) ? replacement : object );
}


const pushToArray = ( array: Array<any>, object: any): Array<any> => {
    if( array.length > 0 ) {
        return [ ...array, object ];  
    } else {
        return [ object ];
    }
}

/*const pushToArrayWithNullCheck = ( currentState, objectToAdd ) => {
    if( currentState === null ) {
        return [ objectToAdd ];
    } else {
        return [
            ...currentState,
            objectToAdd
        ];
    }
};*/

/**
 * finds and pushes a user view settings object into the settings array
 * @param { string } viewIdentifier the unique identifier of the view
 * @param { UserViewSettings } settings UserViewSettings object
 * @param { UserViewSettings} stateSettings the current state of the all visited sites
 */
const pushUserViewSettings = ( viewIdentifier: string, settings: UserViewSettings, stateSettings: UserViewSettingsList ) => {
    return {
            ...stateSettings,
            [ viewIdentifier ]: {
                ...settings,
                activeFilters: settings.activeFilters.map( fil => {
                    if( isDateFilter( fil ) ) {
                        return { ...fil, selectedValue: new Date( fil.selectedValue ) };
                    }
                    return fil;
                })
            },
        };
};